import ProductPanelService from '../../../services/product/ProductPanelService';

/**
 * Resuelve la ruta de imagen de un ítem del menú (o su producto) a una URL
 * usable en un `<img>`:
 *  - Si ya es una URL absoluta / data / blob o una ruta absoluta del sitio,
 *    se devuelve tal cual.
 *  - Si es un nombre de archivo de imagen de producto, se descarga como blob
 *    por el endpoint del panel de productos y se cachea el object URL para no
 *    volver a pedirlo al cambiar de categoría o buscar.
 */
const cache = new Map<string, Promise<string | null>>();

const isDirectUrl = (value: string) =>
  /^(https?:|data:|blob:)/i.test(value) || value.startsWith('/');

/**
 * Nombres con los que el backend marca "sin foto". Se tratan como ausencia de
 * imagen para mostrar el ícono por defecto del restaurante en su lugar.
 */
const PLACEHOLDER_NAMES = /^(default[_-].*|.*[_-]default|no[_-]?image|placeholder|sin[_-]?imagen)\.\w+$/i;

const isPlaceholder = (value: string) => {
  const name = value.split(/[\\/]/).pop() ?? value;
  return PLACEHOLDER_NAMES.test(name);
};

export const resolveRestaurantProductImage = (
  path?: string | null
): Promise<string | null> => {
  const key = path?.trim();

  if (!key || isPlaceholder(key)) return Promise.resolve(null);
  if (isDirectUrl(key)) return Promise.resolve(key);

  const cached = cache.get(key);
  if (cached) return cached;

  const request = ProductPanelService.getProductImage(key).catch(() => null);
  cache.set(key, request);

  // No dejar cacheado un fallo: permite reintentar en el próximo montaje.
  request.then((url) => {
    if (!url) cache.delete(key);
  });

  return request;
};
